import config from '../../config'
import cookiebotClient from '../cookiebotClient'

type FirebaseAnalytics = {
  logEvent: (
    analyticsInstance: unknown,
    eventName: string,
    eventParams?: Record<string, unknown>,
  ) => void
}

type FirebaseAppModule = {
  getApps: () => unknown[]
  initializeApp: (options: Record<string, string>) => unknown
}

type FirebaseAnalyticsModule = FirebaseAnalytics & {
  getAnalytics: (app?: unknown) => unknown
  initializeAnalytics: (
    app: unknown,
    options?: {
      config?: {
        send_page_view?: boolean
      }
    },
  ) => unknown
  isSupported: () => Promise<boolean>
}

type AnalyticsRuntime = {
  analytics: unknown
  logEvent: FirebaseAnalytics['logEvent']
}

class FirebaseAnalyticsClient {
  private analyticsRuntimePromise: Promise<AnalyticsRuntime | null> | null = null

  private hasRequiredConfig = () =>
    Boolean(
      config.firebaseConfig.apiKey &&
        config.firebaseConfig.appId &&
        config.firebaseConfig.projectId &&
        config.firebaseConfig.measurementId,
    )

  getAnalyticsRuntime = async (): Promise<AnalyticsRuntime | null> => {
    if (typeof window === 'undefined' || !this.hasRequiredConfig()) {
      return null
    }

    if (cookiebotClient.isEnabled() && !cookiebotClient.hasStatisticsConsent()) {
      return null
    }

    if (!this.analyticsRuntimePromise) {
      this.analyticsRuntimePromise = (async () => {
        const [{ getApps, initializeApp }, analyticsModule] = await Promise.all([
          import('firebase/app') as Promise<FirebaseAppModule>,
          import('firebase/analytics') as Promise<FirebaseAnalyticsModule>,
        ])

        const analyticsSupported = await analyticsModule.isSupported().catch(() => false)
        if (!analyticsSupported) return null

        const app = getApps()[0] ?? initializeApp(config.firebaseConfig)
        const analytics = analyticsModule.initializeAnalytics(app, {
          config: {
            send_page_view: false,
          },
        })

        return {
          analytics,
          logEvent: analyticsModule.logEvent,
        }
      })()
    }

    return this.analyticsRuntimePromise
  }

  trackPageView = async ({
    pathname,
    href,
    title,
  }: {
    pathname: string
    href: string
    title: string
  }) => {
    const runtime = await this.getAnalyticsRuntime()
    if (!runtime) return

    runtime.logEvent(runtime.analytics, 'page_view', {
      page_title: title,
      page_location: href,
      page_path: pathname,
    })
  }

  trackEvent = async (eventName: string, eventParams?: Record<string, unknown>) => {
    const runtime = await this.getAnalyticsRuntime()
    if (!runtime) return

    runtime.logEvent(runtime.analytics, eventName, eventParams)
  }
}

export default new FirebaseAnalyticsClient()
